/**
 * useHierarchyNavigation Hook
 *
 * Custom React hook for managing hierarchy drilldown navigation state.
 * Maintains breadcrumb history and provides navigation functions.
 *
 * This hook is FRONTEND-ONLY - it manages UI navigation state.
 * When drilldown changes, the parent component should trigger a new
 * backend query to get data for the new scope.
 *
 * HIERARCHY MODEL:
 * - Location is the FOUNDATION (always present)
 * - OrgLevels are OPTIONAL and build UP from Location
 * - Drilldown goes from highest OrgLevel → lower OrgLevels → Location
 * - Empty path = root level (show all at highest level)
 *
 * @module pages/common/dashboards/SPLHDashboard/hooks/useHierarchyNavigation
 */

import { useState, useCallback, useMemo } from 'react';
import {
  buildBreadcrumb,
  navigateToDrilldown,
  navigateBack,
  navigateToLevel,
  navigateToRoot,
  getCurrentNode,
  canNavigateBack,
  getParentNode,
  getHighestLevel,
  hasOrgLevels,
} from '../services/hierarchyService';

/**
 * Drilldown path item structure
 * @typedef {Object} DrilldownPathItem
 * @property {string} id - Node ID
 * @property {string} name - Display name
 * @property {string} level - OrgLevel enum value
 */

/**
 * Breadcrumb item structure (includes level label from metadata)
 * @typedef {Object} BreadcrumbItem
 * @property {string} id - Node ID
 * @property {string} name - Display name
 * @property {string} level - OrgLevel enum value
 * @property {string} levelLabel - Human-readable level label
 */

/**
 * Hook return value
 * @typedef {Object} UseHierarchyNavigationReturn
 * @property {DrilldownPathItem[]} path - Current drilldown path
 * @property {BreadcrumbItem[]} breadcrumb - Breadcrumb trail with level labels
 * @property {DrilldownPathItem|null} currentNode - Current deepest node (null if at root)
 * @property {DrilldownPathItem|null} parentNode - Parent of current node (null if at root or one level deep)
 * @property {boolean} canGoBack - Whether user can navigate back
 * @property {boolean} isAtRoot - Whether user is at root level (empty path)
 * @property {boolean} hasHierarchy - Whether client has OrgLevels (not just Locations)
 * @property {Function} drilldown - Navigate into a child node
 * @property {Function} goBack - Navigate to parent level
 * @property {Function} goToLevel - Navigate to specific breadcrumb level
 * @property {Function} reset - Return to root level
 */

/**
 * Custom hook for managing hierarchy drilldown navigation
 *
 * @param {Object} [hierarchyMetadata] - Backend metadata with level display names
 * @param {DrilldownPathItem[]} [initialPath=[]] - Initial drilldown path
 * @returns {UseHierarchyNavigationReturn} Navigation state and functions
 *
 * @example
 * const {
 *   path,
 *   breadcrumb,
 *   currentNode,
 *   canGoBack,
 *   isAtRoot,
 *   drilldown,
 *   goBack,
 *   goToLevel,
 *   reset
 * } = useHierarchyNavigation(hierarchyMetadata);
 *
 * // Handle row click in hierarchy table
 * const handleRowClick = (node) => {
 *   if (node.hasChildren) {
 *     drilldown(node);
 *   }
 * };
 *
 * // Render breadcrumb
 * <Breadcrumbs>
 *   {breadcrumb.map((item, index) => (
 *     <Link key={item.id} onClick={() => goToLevel(index)}>
 *       {item.levelLabel}: {item.name}
 *     </Link>
 *   ))}
 * </Breadcrumbs>
 */
export function useHierarchyNavigation(hierarchyMetadata = null, initialPath = []) {
  // State for the current drilldown path
  const [path, setPath] = useState(initialPath);

  /**
   * Navigate into a child node (drilldown)
   *
   * @param {Object} node - The node to drill into
   * @param {string} node.id - Node ID
   * @param {string} node.name - Node name
   * @param {string} node.level - Node's OrgLevel
   * @param {boolean} [node.hasChildren] - Whether node has children
   */
  const drilldown = useCallback((node) => {
    if (!node) return;

    const result = navigateToDrilldown(path, node);
    setPath(result.path);
  }, [path]);

  /**
   * Navigate back to parent level (go up one level)
   */
  const goBack = useCallback(() => {
    const newPath = navigateBack(path);
    setPath(newPath);
  }, [path]);

  /**
   * Navigate to a specific breadcrumb level by index
   *
   * @param {number} index - Breadcrumb index (0 = first level after root)
   */
  const goToLevel = useCallback((index) => {
    const newPath = navigateToLevel(path, index);
    setPath(newPath);
  }, [path]);

  /**
   * Reset to root level (empty path = show all at highest level)
   */
  const reset = useCallback(() => {
    const newPath = navigateToRoot();
    setPath(newPath);
  }, []);

  // Derived state (memoized for performance)

  /**
   * Breadcrumb trail with level labels from hierarchyMetadata
   */
  const breadcrumb = useMemo(
    () => buildBreadcrumb(path, hierarchyMetadata),
    [path, hierarchyMetadata]
  );

  /**
   * Current deepest node in the path (null if at root)
   */
  const currentNode = useMemo(
    () => getCurrentNode(path),
    [path]
  );

  /**
   * Parent of current node (null if at root or one level deep)
   */
  const parentNode = useMemo(
    () => getParentNode(path),
    [path]
  );

  /**
   * Whether user can navigate back (not at root)
   */
  const canGoBack = useMemo(
    () => canNavigateBack(path),
    [path]
  );

  /**
   * Whether user is at root level (empty path)
   */
  const isAtRoot = useMemo(
    () => path.length === 0,
    [path]
  );

  /**
   * Whether the client has OrgLevels (not just flat Locations)
   */
  const hasHierarchy = useMemo(
    () => hasOrgLevels(hierarchyMetadata),
    [hierarchyMetadata]
  );

  /**
   * The highest level in the hierarchy (where drilldown starts)
   */
  const highestLevel = useMemo(
    () => getHighestLevel(hierarchyMetadata),
    [hierarchyMetadata]
  );

  return {
    // State
    path,
    breadcrumb,
    currentNode,
    parentNode,
    canGoBack,
    isAtRoot,
    hasHierarchy,
    highestLevel,

    // Actions
    drilldown,
    goBack,
    goToLevel,
    reset,
  };
}

export default useHierarchyNavigation;
